// Classification of upload RPC errors.
//
// HISTORY (incident 2026-06-20 -> 2026-07-27): an out-of-band
// `ALTER ROLE authenticated SET statement_timeout='8s'` made Postgres kill and
// ROLL BACK every CSV import, but the old inline isGatewayTimeout() matched the
// kill message ("canceling statement due to statement timeout") on the word
// "timeout" and reported the upload as a benign gateway timeout that would
// "continue server-side". It does not continue — it is gone. Five weeks of
// uploads were silently lost that way.
//
// The distinction that matters:
//   * STATEMENT timeout (code 57014): the DB killed the transaction. Nothing
//     was saved. This MUST surface as a hard failure.
//   * GATEWAY timeout (504/524/"upstream request timeout"): the HTTP edge gave
//     up waiting but Postgres keeps executing and will commit. Treating this
//     as benign is correct.

export const isStatementTimeout = (err) => {
  if (!err) return false;
  const code = err.code ?? err.status;
  if (String(code) === "57014") return true;
  const message = (err.message || "").toLowerCase();
  return (
    message.includes("statement timeout") ||
    message.includes("canceling statement")
  );
};

export const isGatewayTimeout = (err) => {
  if (!err) return false;
  // A DB-side kill is never a benign gateway timeout, whatever the wording.
  if (isStatementTimeout(err)) return false;
  const rawCode = err.status ?? err.code;
  const numericCode =
    typeof rawCode === "string" ? Number(rawCode) : Number(rawCode ?? NaN);
  if (numericCode === 504 || numericCode === 524) return true;
  const message = (err.message || "").toLowerCase();
  if (message.includes("upstream request timeout")) return true;
  if (message.includes("timeout")) return true;
  if (message.includes("timed out")) return true;
  return false;
};

export const STATEMENT_TIMEOUT_USER_MESSAGE =
  "The database timed out and the upload was ROLLED BACK — no data was saved. " +
  "Retry the upload; if it keeps failing, the file may be too large for one batch.";

// User-facing statement-timeout error. Carries code 57014 so that if it
// cascades through another classification layer (e.g. a .then throw landing
// in the sibling .catch), isStatementTimeout still recognizes it and
// isGatewayTimeout can never re-classify it as benign — its message contains
// "timed out", which the gateway matcher would otherwise match.
export const statementTimeoutError = () => {
  const err = new Error(STATEMENT_TIMEOUT_USER_MESSAGE);
  err.code = "57014";
  return err;
};
